/**
 * Which word games are playable, and the sentence shown for one that is not.
 *
 * Four are built and feed the review schedule; the games row offers more than four, and
 * a learner who taps an unbuilt one is told so rather than shown a board that counts
 * nothing. That message named **word search alone** on all three platforms, long after
 * crossword, matching and spelling bee had shipped — so a learner reaching for minimal
 * pairs was told one game existed when four did, and the product undersold itself in
 * its own UI.
 *
 * The list lives here for the same reason `SESSION_KIND_LABELS` does: it is duplicated
 * by hand in Swift and Kotlin, and three platforms disagreeing about what this product
 * contains is three products. Adding a game means adding it here, and the sentence
 * follows.
 */

/** Game types the games row offers. */
export const GAME_TYPES = [
  'wordsearch',
  'crossword',
  'matching',
  'spelling',
  'minimalpairs',
  'speedmatch',
] as const;
export type GameType = (typeof GAME_TYPES)[number];

/** The ones with a board on every client, each grading into the review schedule. */
export const BUILT_GAMES: readonly GameType[] = ['wordsearch', 'crossword', 'matching', 'spelling'];

export const GAME_TITLES: Readonly<Record<GameType, string>> = {
  wordsearch: 'Word search',
  crossword: 'Crossword',
  matching: 'Matching',
  spelling: 'Spelling bee',
  minimalpairs: 'Minimal pairs',
  speedmatch: 'Speed match',
};

export function isGameBuilt(type: string): boolean {
  return (BUILT_GAMES as readonly string[]).includes(type);
}

/**
 * What to say when a learner opens a game that does not exist yet.
 *
 * Names every game that *does*, so the sentence cannot go stale by omission the way
 * "Word search is" did — adding a game to `BUILT_GAMES` rewrites it.
 */
export function gamesNotBuiltMessage(): string {
  const titles = BUILT_GAMES.map((type) => GAME_TITLES[type]);
  const last = titles[titles.length - 1];
  const list = titles.length === 1 ? last : `${titles.slice(0, -1).join(', ')} and ${last}`;
  return `This one isn't built yet. ${list} are, and they all feed your review schedule.`;
}
